import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  ogType?: string;
  schema?: object;
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  schema
}: SEOProps) {
  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', ogType, true);
    
    if (canonical) {
      updateMetaTag('og:url', `${import.meta.env.VITE_SITE_URL || ''}${canonical}`, true);
    }

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);

    // Canonical URL
    if (canonical) {
      let linkElement = document.querySelector('link[rel="canonical"]');
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.setAttribute('rel', 'canonical');
        document.head.appendChild(linkElement);
      }
      linkElement.setAttribute('href', `${import.meta.env.VITE_SITE_URL || ''}${canonical}`);
    }

    // Schema.org JSON-LD - Remove old scripts and add new one
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    if (schema) {
      const scriptElement = document.createElement('script');
      scriptElement.setAttribute('type', 'application/ld+json');
      scriptElement.textContent = JSON.stringify(schema);
      document.head.appendChild(scriptElement);
    }

    // Cleanup function
    return () => {
      // Remove schema script on unmount
      const scriptElements = document.querySelectorAll('script[type="application/ld+json"]');
      scriptElements.forEach(script => script.remove());
    };
  }, [title, description, keywords, canonical, ogType, schema]);
}

// Helper function to generate Organization schema
export function getOrganizationSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || '';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Million Dollar Contractors',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description: 'Digital marketing agency specializing in home service contractors. Expert web design, SEO, social media, and AI integration for electrical, HVAC, plumbing, roofing, and landscaping businesses.',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Boca Raton',
      addressRegion: 'FL',
      addressCountry: 'US'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+1-561-555-0100',
      contactType: 'customer service',
      areaServed: 'US',
      availableLanguage: 'English'
    },
    sameAs: [
      'https://www.facebook.com/milliondollarcontractors',
      'https://www.linkedin.com/company/milliondollarcontractors',
      'https://twitter.com/milliondollarcontractors'
    ]
  };
}

// Helper function to generate LocalBusiness schema
export function getLocalBusinessSchema(location: string, region: string) {
  const siteUrl = import.meta.env.VITE_SITE_URL || '';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: `Million Dollar Contractors - ${location}`,
    image: `${siteUrl}/logo.png`,
    '@id': siteUrl,
    url: siteUrl,
    telephone: '+1-561-555-0100',
    address: {
      '@type': 'PostalAddress',
      addressLocality: location,
      addressRegion: region,
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 26.3586,
      longitude: -80.0831
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday'
      ],
      opens: '09:00',
      closes: '18:00'
    },
    priceRange: '$$',
    areaServed: {
      '@type': 'City',
      name: location
    }
  };
}

// Helper function to generate Service schema
export function getServiceSchema(serviceName: string, description: string) {
  const siteUrl = import.meta.env.VITE_SITE_URL || '';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: serviceName,
    provider: {
      '@type': 'Organization',
      name: 'Million Dollar Contractors',
      url: siteUrl
    },
    description: description,
    areaServed: {
      '@type': 'Country',
      name: 'United States'
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Digital Marketing Services',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: serviceName
          }
        }
      ]
    }
  };
}

// Helper function to generate WebPage schema
export function getWebPageSchema(title: string, description: string, url: string) {
  const siteUrl = import.meta.env.VITE_SITE_URL || '';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description: description,
    url: `${siteUrl}${url}`,
    publisher: {
      '@type': 'Organization',
      name: 'Million Dollar Contractors',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`
      }
    }
  };
}

// Helper function to generate FAQPage schema
export function getFAQSchema(faqs: Array<{ question: string; answer: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
}

// Helper function to generate BreadcrumbList schema
export function getBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  const siteUrl = import.meta.env.VITE_SITE_URL || '';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`
    }))
  };
}

// Helper function to generate Review schema
export function getReviewSchema(reviews: Array<{
  author: string;
  rating: number;
  reviewBody: string;
  datePublished: string;
}>) {
  const siteUrl = import.meta.env.VITE_SITE_URL || '';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Million Dollar Contractors',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: reviews.length.toString(),
      bestRating: '5',
      worstRating: '1'
    },
    review: reviews.map(review => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: review.author
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.rating.toString(),
        bestRating: '5',
        worstRating: '1'
      },
      reviewBody: review.reviewBody,
      datePublished: review.datePublished
    }))
  };
}

// Helper function to generate multiple schemas
export function combineSchemas(...schemas: object[]) {
  return {
    '@context': 'https://schema.org',
    '@graph': schemas
  };
}
